import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { useTranslation } from "react-i18next";

import { startAuthentication } from "@simplewebauthn/browser";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CircleAlertIcon,
  InfoIcon,
  MessageCircleIcon,
  XIcon,
} from "lucide-react";

import {
  type LoginMfaChallenge,
  MFA_METHOD_ORDER,
  type MfaMethod,
  getPasskeyUserMessage,
  isLoginMfaRequired,
} from "simplydo/core/mfa";
import { maskEmailAddress } from "simplydo/core/users";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api from "api";

import { MFAAppLinks } from "components/lib/Mfa/MFAAppLinks";
import ModalCarousel, {
  type ModalCarouselAction,
  type ModalCarouselScreen,
} from "components/lib/ModalCarousel";

import { MfaMethodPicker } from "./MfaMethodPicker";

const openChatwoot = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

type LoginPayload = Parameters<typeof api.auth.login>[0];

type MfaLoginStage = "picker" | "otp" | "passkey" | "recovery";

type MfaLoginFlowProps = {
  email: string;
  password: string;
  organisationId?: string;
  challenge: LoginMfaChallenge;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  onSuccess: () => void;
  onCancel: () => void;
};

const getInitialMethods = (challenge: LoginMfaChallenge) => {
  const available = (challenge.availableMethods ?? ["totp"]) as MfaMethod[];
  return MFA_METHOD_ORDER.filter((method) => available.includes(method));
};

const getInitialMethod = (
  challenge: LoginMfaChallenge,
  methods: MfaMethod[],
): MfaMethod | null => {
  if (challenge.selectedMethod) {
    return challenge.selectedMethod as MfaMethod;
  }
  if (methods.length === 1) {
    return methods[0] ?? null;
  }
  return null;
};

export function MfaLoginFlow({
  email,
  password,
  organisationId,
  challenge,
  loading,
  setLoading,
  onSuccess,
  onCancel,
}: MfaLoginFlowProps) {
  const { t } = useTranslation();
  const passkeyStartedRef = useRef(false);
  const initialMethods = getInitialMethods(challenge);
  const initialMethod = getInitialMethod(challenge, initialMethods);

  const [errorMessage, setErrorMessage] = useState("");
  const [exchangeToken, setExchangeToken] = useState(
    () => challenge.exchangeToken ?? "",
  );
  const [availableMethods, setAvailableMethods] =
    useState<MfaMethod[]>(initialMethods);
  const [selectedMethod, setSelectedMethod] = useState<MfaMethod | null>(
    initialMethod,
  );
  const [mfaPickerOpen, setMfaPickerOpen] = useState(
    () => initialMethods.length > 1 && !challenge.selectedMethod,
  );
  const [otpModal, setOtpModal] = useState(
    () => initialMethod === "totp" || initialMethod === "emailOtp",
  );
  const [passkeyModal, setPasskeyModal] = useState(
    () => initialMethod === "passkey" && initialMethods.length > 1,
  );
  const [recoveryCodesModal, setRecoveryCodesModal] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [otpRecoveryCode, setOtpRecoveryCode] = useState("");
  const [userHasRecoveryCodes, setUserHasRecoveryCodes] = useState(
    () => challenge.userHasRecoveryCodes ?? null,
  );
  const [passkeyOptions, setPasskeyOptions] = useState(
    () => challenge.passkeyOptions,
  );

  const canChooseMfaMethod = availableMethods.length > 1;

  const applyChallenge = useCallback(
    (data: LoginMfaChallenge) => {
      const methods = getInitialMethods(data);
      setExchangeToken(data.exchangeToken ?? "");
      setAvailableMethods(methods);
      setUserHasRecoveryCodes(data.userHasRecoveryCodes ?? null);
      setPasskeyOptions(data.passkeyOptions);
      passkeyStartedRef.current = false;

      if (methods.length > 1 && !data.selectedMethod) {
        setSelectedMethod(null);
        setOtpModal(false);
        setPasskeyModal(false);
        setRecoveryCodesModal(false);
        setMfaPickerOpen(true);
        setLoading(false);
        return;
      }

      const method = (data.selectedMethod ?? methods[0]) as MfaMethod;
      setMfaPickerOpen(false);
      setSelectedMethod(method);
      if (method === "totp" || method === "emailOtp") {
        setPasskeyModal(false);
        setOtpModal(true);
        setLoading(false);
      } else if (method === "passkey") {
        setOtpModal(false);
        if (methods.length > 1) {
          setPasskeyModal(true);
          setLoading(false);
        }
      }
    },
    [setLoading],
  );

  const completeLogin = useCallback(
    (loginData: LoginPayload) => {
      api.auth.login(
        loginData,
        (data) => {
          if (isLoginMfaRequired(data)) {
            applyChallenge(data);
            return;
          }
          setRecoveryCodesModal(false);
          setOtpModal(false);
          setPasskeyModal(false);
          setLoading(false);
          onSuccess();
        },
        (err) => {
          setLoading(false);
          setErrorMessage(err.message);
          if (err.errorCode === "INCORRECT_VERIFICATION_CODE") {
            setOtpModal(true);
            setRecoveryCodesModal(false);
            return;
          }
          setOtpModal(false);
          setRecoveryCodesModal(false);
          setMfaCode("");
          setOtpRecoveryCode("");
        },
      );
    },
    [applyChallenge, onSuccess, setLoading],
  );

  const returnToMfaMethodPicker = useCallback(() => {
    setOtpModal(false);
    setPasskeyModal(false);
    setRecoveryCodesModal(false);
    setMfaCode("");
    setOtpRecoveryCode("");
    setErrorMessage("");
    setSelectedMethod(null);
    passkeyStartedRef.current = false;
    setLoading(false);
    setMfaPickerOpen(true);
  }, [setLoading]);

  const runPasskeyLogin = useCallback(
    async (options?: Record<string, unknown>) => {
      try {
        const optionsJSON = (options ??
          passkeyOptions ??
          (await new Promise<Record<string, unknown>>((resolve, reject) => {
            api.auth.login(
              {
                email,
                password,
                exchangeToken,
                mfaMethod: "passkey",
                org: organisationId,
              },
              (data) => {
                const nextOptions = (
                  data as { passkeyOptions?: Record<string, unknown> }
                ).passkeyOptions;
                if (nextOptions) {
                  resolve(nextOptions);
                  return;
                }
                reject(new Error("No passkey options returned"));
              },
              reject,
            );
          }))) as unknown as Parameters<
          typeof startAuthentication
        >[0]["optionsJSON"];
        const credential = await startAuthentication({ optionsJSON });
        setLoading(true);
        completeLogin({
          email,
          password,
          exchangeToken,
          passkeyCredential: credential as unknown as Record<string, never>,
          org: organisationId,
        });
      } catch (err) {
        setLoading(false);
        passkeyStartedRef.current = false;
        const message = getPasskeyUserMessage(err, t);
        if (message === null) {
          setPasskeyModal(false);
          if (canChooseMfaMethod) {
            returnToMfaMethodPicker();
          }
          return;
        }
        setErrorMessage(message);
      }
    },
    [
      canChooseMfaMethod,
      completeLogin,
      email,
      exchangeToken,
      organisationId,
      passkeyOptions,
      password,
      returnToMfaMethodPicker,
      setLoading,
      t,
    ],
  );

  const selectMfaMethod = useCallback(
    (method: MfaMethod) => {
      // The picker stays on screen until the challenge comes back, so the
      // modal never closes between stages.
      setSelectedMethod(method);
      setErrorMessage("");
      setLoading(true);
      completeLogin({
        email,
        password,
        exchangeToken,
        mfaMethod: method,
        org: organisationId,
      });
    },
    [completeLogin, email, exchangeToken, organisationId, password, setLoading],
  );

  const submitMfa = useCallback(() => {
    setErrorMessage("");
    setLoading(true);
    completeLogin({
      email,
      password,
      exchangeToken,
      otp: selectedMethod === "totp" || !selectedMethod ? mfaCode : undefined,
      emailOtpCode: selectedMethod === "emailOtp" ? mfaCode : undefined,
      org: organisationId,
    });
  }, [
    completeLogin,
    email,
    exchangeToken,
    mfaCode,
    organisationId,
    password,
    selectedMethod,
    setLoading,
  ]);

  const submitRecoveryCode = useCallback(() => {
    setErrorMessage("");
    setLoading(true);
    completeLogin({
      email,
      password,
      exchangeToken,
      otpRecoveryCode,
      org: organisationId,
    });
  }, [
    completeLogin,
    email,
    exchangeToken,
    organisationId,
    otpRecoveryCode,
    password,
    setLoading,
  ]);

  useEffect(() => {
    if (
      selectedMethod === "passkey" &&
      !mfaPickerOpen &&
      !passkeyModal &&
      availableMethods.length === 1 &&
      exchangeToken &&
      !passkeyStartedRef.current
    ) {
      passkeyStartedRef.current = true;
      void runPasskeyLogin(passkeyOptions);
    }
  }, [
    availableMethods.length,
    exchangeToken,
    mfaPickerOpen,
    passkeyModal,
    passkeyOptions,
    runPasskeyLogin,
    selectedMethod,
  ]);

  const closeMfa = () => {
    setOtpModal(false);
    setPasskeyModal(false);
    setRecoveryCodesModal(false);
    setMfaCode("");
    setOtpRecoveryCode("");
    setErrorMessage("");
    setLoading(false);
    onCancel();
  };

  const stage: MfaLoginStage | null = mfaPickerOpen
    ? "picker"
    : otpModal
      ? "otp"
      : passkeyModal
        ? "passkey"
        : recoveryCodesModal
          ? "recovery"
          : null;

  const errorBanner =
    errorMessage && !loading ? (
      <Message size="small" color="orange">
        <MessageHeader>
          <Icon icon={CircleAlertIcon} />
          {errorMessage}
        </MessageHeader>
      </Message>
    ) : null;

  const chooseAnotherMethodAction: ModalCarouselAction[] = canChooseMfaMethod
    ? [
        {
          label: t("entry.mfa.chooseAnotherMethod"),
          icon: ArrowLeftIcon,
          onClick: returnToMfaMethodPicker,
        },
      ]
    : [];

  const cancelAction: ModalCarouselAction = {
    label: t("generic.cancel"),
    onClick: closeMfa,
  };

  const stageScreens: Record<MfaLoginStage, ModalCarouselScreen> = {
    picker: {
      title: t("entry.mfa.chooseMethod"),
      content: (
        <>
          <MfaMethodPicker
            methods={availableMethods}
            pendingMethod={loading ? selectedMethod : null}
            onSelect={selectMfaMethod}
          />
          {errorBanner}
        </>
      ),
      actions: [cancelAction],
    },
    otp: {
      title: selectedMethod
        ? t(`entry.mfa.method.${selectedMethod}`)
        : t("entry.mfa.title"),
      content: (
        <>
          {selectedMethod === "emailOtp" ? (
            <p>
              {t("entry.mfa.emailOtpLoginInfo", {
                maskedEmail: maskEmailAddress(email),
              })}
            </p>
          ) : (
            <p>{t("entry.mfa.totpLoginInfo")}</p>
          )}
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Input
              onKeyDown={(e: KeyboardEvent) => {
                if (e.keyCode === 13) {
                  e.preventDefault();
                  e.stopPropagation();
                  submitMfa();
                }
              }}
              onChange={(e) => setMfaCode(e.target.value)}
              value={mfaCode}
              placeholder="123456"
              size="large"
              autoFocus
            />
          </div>
          {errorBanner}
          {selectedMethod === "totp" ? (
            <>
              <Divider hidden />
              <Message color="blue" size="small">
                <MessageHeader>
                  <Icon icon={InfoIcon} />
                  Authenticator apps
                </MessageHeader>
                <MessageContent>
                  {t("entry.mfa.authenticatorLoginHelpBefore")} <MFAAppLinks />{" "}
                  {t("entry.mfa.authenticatorLoginHelpAfter")}
                </MessageContent>
              </Message>
              <Divider />
              <Message size="small">
                <MessageHeader>{t("entry.mfa.lostAccessTitle")}</MessageHeader>
                <MessageContent>
                  <ol style={{ margin: "0.5em 0 0", paddingLeft: "1.5em" }}>
                    <li style={{ marginBottom: "0.75em" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "0.5em",
                        }}
                      >
                        <span>
                          <strong>{t("entry.mfa.useBackupCodeLabel")}</strong>{" "}
                          {t("entry.mfa.useBackupCodeInfo")}
                        </span>
                        <Button
                          size="small"
                          disabled={!userHasRecoveryCodes}
                          onClick={() => {
                            setMfaCode("");
                            setErrorMessage("");
                            setRecoveryCodesModal(true);
                            setOtpModal(false);
                          }}
                        >
                          {t("entry.mfa.useBackupCodeAction")}
                        </Button>
                      </div>
                    </li>
                    <li>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "0.5em",
                        }}
                      >
                        <span>
                          <strong>{t("entry.mfa.contactUsLabel")}</strong>{" "}
                          {t("entry.mfa.contactUsInfo")}
                        </span>
                        <Button size="small" onClick={openChatwoot}>
                          <Icon icon={MessageCircleIcon} />
                          {t("entry.mfa.contactUsAction")}
                        </Button>
                      </div>
                    </li>
                  </ol>
                </MessageContent>
              </Message>
            </>
          ) : null}
        </>
      ),
      actions: [
        ...chooseAnotherMethodAction,
        cancelAction,
        {
          label: t("generic.continue"),
          icon: CheckIcon,
          primary: true,
          disabled: !mfaCode,
          loading,
          onClick: submitMfa,
        },
      ],
    },
    passkey: {
      title: t("entry.mfa.method.passkey"),
      content: (
        <>
          <p style={{ marginTop: 0 }}>{t("entry.mfa.passkeyLoginInfo")}</p>
          {errorBanner}
        </>
      ),
      actions: [
        ...chooseAnotherMethodAction,
        cancelAction,
        {
          label: t("entry.mfa.continueWithPasskey"),
          primary: true,
          icon: ArrowRightIcon,
          loading,
          onClick: () => {
            setErrorMessage("");
            void runPasskeyLogin(passkeyOptions);
          },
        },
      ],
    },
    recovery: {
      title: t("entry.mfa.useRecoveryCode"),
      content: (
        <>
          <p>{t("entry.mfa.recoveryLoginInfo")}</p>
          <FormField>
            <FormLabel>{t("entry.mfa.recoveryCode")}</FormLabel>
            <Input
              fluid
              onKeyDown={(e: KeyboardEvent) => {
                if (e.keyCode === 13) {
                  e.preventDefault();
                  e.stopPropagation();
                  submitRecoveryCode();
                }
              }}
              onChange={(e) => setOtpRecoveryCode(e.target.value)}
              value={otpRecoveryCode}
              placeholder={t("entry.mfa.recoveryPlaceholder")}
              autoFocus
            />
          </FormField>
          {errorBanner}
        </>
      ),
      actions: [
        {
          label: t("generic.back"),
          icon: ArrowLeftIcon,
          onClick: () => {
            setOtpRecoveryCode("");
            setErrorMessage("");
            setRecoveryCodesModal(false);
            setOtpModal(true);
          },
        },
        {
          label: t("entry.mfa.verifyRecoveryCode"),
          primary: true,
          disabled: !otpRecoveryCode,
          loading,
          onClick: submitRecoveryCode,
        },
      ],
    },
  };

  return (
    <ModalCarousel
      title={t("entry.mfa.title")}
      open={stage !== null}
      screens={stage ? [stageScreens[stage]] : []}
      onClose={closeMfa}
    />
  );
}
